import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Button } from '../ui/Button';
import { Chip } from '../ui/Chip';
import { Spinner } from '../ui/Spinner';
import { EmptyState } from '../ui/EmptyState';
import { formatPrice } from '../lib/format';
import { PAGE_SIZE_OPTIONS, usePageSize, type PageSize } from '../lib/page-size';
import { getGarment } from '../lib/api';
import {
  matchGarments,
  SEARCH_RESULT_LIMIT,
  useCatalog,
  useCategories,
  useGarmentIndex,
} from './useCatalog';
import type { GarmentIndexEntry, GarmentSummary } from '../lib/types';

interface CatalogBrowserProps {
  onSelect: (garment: GarmentSummary) => void;
  /** Garments already queued or generated — shown as such on the tile. */
  statusFor?: (garmentId: string) => 'queued' | 'generating' | 'done' | 'failed' | undefined;
  footer?: ReactNode;
}

export function CatalogBrowser({ onSelect, statusFor, footer }: CatalogBrowserProps) {
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [pageSize, setPageSize] = usePageSize('catalog');
  const categoriesQuery = useCategories();
  const catalog = useCatalog(selectedCategories, pageSize);

  const [search, setSearch] = useState('');
  const [opening, setOpening] = useState<string | null>(null);
  const [openFailed, setOpenFailed] = useState(false);
  const index = useGarmentIndex();

  /**
   * Search **replaces** the browse grid rather than filtering it, and while it is
   * on, the chips and the page-size control are hidden.
   *
   * ⚠️ **Search deliberately ignores the category chips.** The index carries names
   * and ids only — no category — so combining the two would mean either a second
   * field in the payload or a round trip, and searching a name the customer can
   * already see on a tag is not a case that needs narrowing. Typing searches the
   * whole catalogue; clearing the box restores the chips exactly as they were,
   * since `selectedCategories` is never touched here.
   */
  const searching = search.trim() !== '';
  const matches = matchGarments(index.data?.garments ?? [], search);

  const sentinelRef = useRef<HTMLDivElement>(null);

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = catalog;

  /**
   * Infinite scroll via IntersectionObserver rather than a scroll listener —
   * a scroll handler fires continuously during iOS momentum scrolling and
   * makes the grid stutter on a mid-range tablet.
   *
   * ⚠️ Depends on the three values it actually reads, **not on `catalog`.** The
   * query object is a fresh reference on every render, so depending on it tore
   * the observer down and rebuilt it constantly — which on a fast scroll can
   * mean the sentinel passes through a moment when no observer is watching, and
   * the grid just stops loading with more garments to come.
   */
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
      },
      { rootMargin: '400px' },
    );

    observer.observe(node);
    return () => observer.disconnect();
    /**
     * ⚠️ **`searching` is in here because the sentinel unmounts during a search.**
     * None of the other three values change when the box is cleared, so without it
     * this effect would not re-run, the ref would still point at the removed node,
     * and the restored grid would scroll to its end and simply stop loading —
     * the same silent failure the comment above describes, from the other side.
     */
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, searching]);

  /**
   * A search result carries an id and a name, so opening one fetches the garment
   * it stands for — the round trip that keeps images out of the index. It is
   * `onSelect` either way, so a result opens the preview on the garments screen
   * and starts a try-on on the try-on screen, with no branching here.
   */
  async function openSearchResult(entry: GarmentIndexEntry): Promise<void> {
    setOpening(entry.garmentId);
    setOpenFailed(false);

    try {
      onSelect(await getGarment(entry.garmentId));
    } catch {
      // Deleted mid-session, or the network dropped between typing and tapping.
      // Silence here would read as a dead button.
      setOpenFailed(true);
    } finally {
      setOpening(null);
    }
  }

  function toggleCategory(category: string): void {
    setSelectedCategories((current) =>
      current.includes(category)
        ? current.filter((item) => item !== category)
        : [...current, category],
    );
    // No offset reset needed here — `categories` is part of the query key, so a
    // different filter is a different query that starts from page 0 by itself.
  }

  const garments = catalog.data?.pages.flatMap((page) => page.garments) ?? [];
  const categories = categoriesQuery.data?.categories ?? [];

  return (
    <div className="flex flex-1 flex-col">
      <div className="px-4 pt-3">
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search garments by name"
          aria-label="Search garments by name"
          // `search` rather than `text` for the clear affordance iOS puts in the
          // field for free — the fastest way back to the grid on a tablet.
          className="min-h-(--size-control-sm) w-full rounded-md border border-(--color-border-default) bg-surface px-3 py-2 text-sm text-primary placeholder:text-tertiary"
        />
      </div>

      {!searching && categories.length > 0 && (
        <div className="rail flex gap-2 px-4 py-3">
          {categories.map((category) => (
            <Chip
              key={category}
              label={category}
              selected={selectedCategories.includes(category)}
              onToggle={() => toggleCategory(category)}
            />
          ))}
        </div>
      )}

      {/*
        How many garments each fetch pulls. Changing it re-keys the query, so the
        grid restarts from the top rather than stitching two sizes together.

        Gated on `!searching` by rendering, not by the `hidden` attribute — this is
        a `flex` element, and Tailwind's `.flex` utility comes after preflight's
        `[hidden]`, so at equal specificity the display would win and the control
        would stay on screen through a search.
      */}
      {!searching && (
        <div className="flex items-center justify-end gap-2 px-4 pb-2">
          <label htmlFor="catalog-page-size" className="text-xs text-tertiary">
            Load per page
          </label>
          <select
            id="catalog-page-size"
            value={pageSize}
            onChange={(event) => setPageSize(Number(event.target.value) as PageSize)}
            className="min-h-(--size-control-sm) rounded-md border border-(--color-border-default) bg-surface px-2 text-sm text-primary"
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      )}

      {searching ? (
        <SearchResults
          matches={matches}
          search={search}
          isPending={index.isPending}
          isError={index.isError}
          openFailed={openFailed}
          opening={opening}
          onOpen={openSearchResult}
        />
      ) : catalog.isPending ? (
        <div className="flex flex-1 items-center justify-center py-16 text-tertiary">
          <Spinner label="Loading garments" />
        </div>
      ) : catalog.isError ? (
        <EmptyState
          title="Could not load the catalogue"
          description="Check the connection and pull to try again."
        />
      ) : garments.length === 0 ? (
        <EmptyState
          title="Nothing here yet"
          description={
            selectedCategories.length > 0
              ? 'No garments in the selected categories.'
              : 'This store has no garments in its catalogue.'
          }
        />
      ) : (
        <div className="grid grid-cols-2 gap-3 px-4 pb-4 sm:grid-cols-3 lg:grid-cols-4">
          {garments.map((garment) => (
            <GarmentTile
              key={garment.garmentId}
              garment={garment}
              status={statusFor?.(garment.garmentId)}
              onSelect={() => onSelect(garment)}
            />
          ))}
        </div>
      )}

      {/* Paging furniture, and only the paged grid has any use for it. */}
      {!searching && <div ref={sentinelRef} className="h-px" aria-hidden="true" />}

      {/*
        ⚠️ The button is not redundant with the observer above it. An
        IntersectionObserver cannot fire when the sentinel never enters the
        viewport — a short first page on a tall screen, or a grid whose images
        have not laid out yet — and when that happens the old grid simply ended,
        with no way to ask for more. This is the manual path, and it is also what
        makes "keep scrolling to the end" true rather than usually true.
      */}
      {!searching && garments.length > 0 && (
        <div className="flex justify-center px-4 py-6">
          {catalog.isFetchingNextPage ? (
            <span className="text-tertiary">
              <Spinner label="Loading more" />
            </span>
          ) : catalog.hasNextPage ? (
            <Button variant="secondary" size="md" onClick={() => void catalog.fetchNextPage()}>
              Load more
            </Button>
          ) : (
            // Stating the end explicitly. Without it a grid that stops looks
            // identical to one that failed to load the next page.
            <p className="text-xs text-tertiary">
              {garments.length} garment{garments.length === 1 ? '' : 's'} — that&rsquo;s everything
            </p>
          )}
        </div>
      )}

      {footer}
    </div>
  );
}

interface SearchResultsProps {
  matches: GarmentIndexEntry[];
  search: string;
  isPending: boolean;
  isError: boolean;
  openFailed: boolean;
  opening: string | null;
  onOpen: (entry: GarmentIndexEntry) => void;
}

/**
 * Matching names, as a list — **no images, by construction.**
 *
 * The index has no urls to render, which is what lets it be fetched whole: a
 * hundred matches cost a hundred short strings rather than a hundred R2 fetches
 * on the one screen showing when a customer walks up. The picture arrives when a
 * name is tapped, one garment at a time.
 *
 * ⚠️ **Every branch here is distinct on purpose.** "Index still loading", "index
 * failed", and "nothing matched" look identical if you collapse them into one
 * empty state — and the third is the only one where the customer should try a
 * different word rather than wait or retry.
 */
function SearchResults({
  matches,
  search,
  isPending,
  isError,
  openFailed,
  opening,
  onOpen,
}: SearchResultsProps) {
  if (isPending) {
    return (
      <div className="flex flex-1 items-center justify-center py-16 text-tertiary">
        <Spinner label="Loading garments" />
      </div>
    );
  }

  if (isError) {
    return (
      <EmptyState
        title="Could not load the catalogue"
        description="Check the connection and pull to try again."
      />
    );
  }

  if (matches.length === 0) {
    return (
      <EmptyState
        title="No garments match"
        description={`Nothing in this store is named like “${search.trim()}”. Try a shorter word.`}
      />
    );
  }

  const shown = matches.slice(0, SEARCH_RESULT_LIMIT);

  return (
    <div className="flex flex-1 flex-col gap-2 px-4 pb-4">
      {openFailed && (
        <p className="text-xs text-tertiary">
          That garment could not be opened. It may have just been removed.
        </p>
      )}

      {shown.map((entry) => (
        <button
          key={entry.garmentId}
          type="button"
          onClick={() => onOpen(entry)}
          // Disabled only while *this* row is loading. Disabling the whole list
          // on any tap makes a slow connection feel like a frozen screen.
          disabled={opening === entry.garmentId}
          className="hoverable flex w-full items-center justify-between gap-3 rounded-lg bg-subtle px-4 py-3 text-left shadow-card transition-transform active:scale-98"
        >
          <span className="min-w-0 text-sm font-semibold text-primary">{entry.name}</span>
          {opening === entry.garmentId && (
            <span className="shrink-0 text-tertiary">
              <Spinner label="Opening" />
            </span>
          )}
        </button>
      ))}

      {/*
        Stating the count for the same reason the grid states its end: a list that
        stops looks identical to one that was truncated.
      */}
      <p className="px-1 pt-2 text-xs text-tertiary">
        {matches.length > shown.length
          ? `Showing ${shown.length} of ${matches.length} matches — keep typing to narrow.`
          : `${matches.length} match${matches.length === 1 ? '' : 'es'}`}
      </p>
    </div>
  );
}

interface GarmentTileProps {
  garment: GarmentSummary;
  status?: 'queued' | 'generating' | 'done' | 'failed';
  onSelect: () => void;
}

function GarmentTile({ garment, status, onSelect }: GarmentTileProps) {
  const price = formatPrice(garment.price);

  return (
    <button
      type="button"
      onClick={onSelect}
      className="hoverable relative flex flex-col overflow-hidden rounded-lg bg-subtle text-left shadow-card transition-transform active:scale-98"
    >
      <div className="relative aspect-3/4 w-full">
        {garment.displayImageUrl ? (
          <img
            src={garment.displayImageUrl}
            alt={garment.name}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-xs text-tertiary">
            No image
          </div>
        )}

        {status && (
          <span className="absolute top-2 right-2 rounded-full bg-inverse/90 px-2 py-1 text-xs font-medium text-on-inverse">
            {status === 'done'
              ? 'Ready'
              : status === 'failed'
                ? 'Failed'
                : status === 'generating'
                  ? 'Working…'
                  : 'Queued'}
          </span>
        )}

        {/*
          Caption over the foot of the tile rather than in a strip below it —
          same arrangement as `GarmentPreview`, so a garment reads identically
          whether it is in the grid or opened large. Name left, price right: the
          split keeps a long name from pushing the price out of alignment down
          the column, which is what makes a grid of prices scannable at all.
        */}
        <div
          className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 px-2 pt-8 pb-2"
          style={{
            backgroundImage:
              'linear-gradient(to top, rgb(0 0 0 / 0.78) 0%, rgb(0 0 0 / 0.4) 50%, rgb(0 0 0 / 0) 100%)',
          }}
        >
          <span className="line-clamp-2 min-w-0 text-xs font-semibold text-white">
            {garment.name}
          </span>
          {price && (
            <span className="shrink-0 text-xs font-semibold text-white tabular-nums">{price}</span>
          )}
        </div>
      </div>
    </button>
  );
}
